import { Typography } from '@euler/app/components/typography';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import {
  ApdmBuildCardKind,
  ApdmBuildStep,
  ApdmCouponCampaignSummary,
} from '@euler/model/apdm';
import { CirclePlus, Ticket } from 'lucide-react-native';
import { memo, useContext, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  AiChatPlanContext,
  useAiChatActions,
  useAiPlanStepOpen,
} from '../chat/AiChatActions';
import { CardLoading, CardNotice, ChatCard } from '../components/ChatCard';
import { PrimaryButton, SecondaryButton } from '../components/PrimaryButton';
import { couponItemText, couponPeriodText } from '../components/couponText';
import { aiColors, tint } from '../theme';
import { CouponCampaignDetailSheet } from './CouponCampaignDetailSheet';
import { isAiMessageId, usePlanCard } from './usePlanCard';

// The composed campaign's place among the choices, which are ids.
const kComposed = 'composed';

/**
 * Which coupon campaign the plan offers: one composed for it (first,
 * preselected) or one of the store's, as its reply listed them; a tap
 * settles which (spec §11.4.2).
 */
export const PlanCouponChoiceCard = memo(
  ({ messageId, streaming }: { messageId?: string; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    if (streaming || !isAiMessageId(messageId)) return null;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法为活动选择优惠券。" />;
    return <ChoiceBody key={messageId} messageId={messageId} />;
  },
);

function ChoiceBody({ messageId }: { messageId: string }) {
  const { rpx } = useResponsiveContext();
  const chat = useAiChatActions();
  const interactive = useAiPlanStepOpen(ApdmBuildStep.Which);
  const state = usePlanCard(messageId, ApdmBuildCardKind.CouponChoice);
  const suggested = useContext(AiChatPlanContext)?.suggestedOffer;
  const [tapped, setTapped] = useState<string | null>(null);
  const [detail, setDetail] = useState<ApdmCouponCampaignSummary | null>(null);
  if (state.kind === 'loading')
    return (
      <ChatCard icon={Ticket} title="选择优惠券活动">
        <CardLoading label="正在列出优惠券活动…" />
      </ChatCard>
    );
  if (
    state.kind === 'missing' ||
    state.view.card !== ApdmBuildCardKind.CouponChoice
  )
    return (
      <ChatCard icon={Ticket} title="选择优惠券活动">
        <CardNotice text="没有读取到优惠券活动，请稍后重新打开对话。" />
      </ChatCard>
    );
  const { choices, composed } = state.view.couponChoice;
  const composedTitle = composed
    ? composed.proposal.coupon.title ?? composed.proposal.coupon.name
    : null;
  // The composed campaign, else the one the AI inferred, is preselected; a
  // tap overrides it.
  const selected =
    tapped ??
    (composed
      ? kComposed
      : interactive && suggested?.kind === 'coupon_campaign'
      ? suggested.couponCampaignId
      : null);
  const chosen = choices.find(choice => choice.id === selected) ?? null;
  return (
    <ChatCard
      icon={Ticket}
      title="选择优惠券活动"
      subtitle={
        choices.length
          ? '选一个店里的优惠券活动，或新建一个。'
          : composed
          ? '为这次活动新建的优惠券活动，活动发起时创建。'
          : '店里还没有可用的优惠券活动，可以新建一个。'
      }
    >
      {composed && composedTitle ? (
        <Pressable
          accessibilityRole="radio"
          accessibilityState={{
            selected: selected === kComposed,
            disabled: !interactive,
          }}
          disabled={!interactive}
          onPress={() => setTapped(kComposed)}
          style={{
            padding: rpx(12),
            borderRadius: rpx(12),
            borderWidth: selected === kComposed ? 2 : 1,
            borderColor:
              selected === kComposed ? aiColors.primary : aiColors.border,
            backgroundColor:
              selected === kComposed
                ? tint(aiColors.primary, 0.04)
                : aiColors.card,
            gap: rpx(4),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(6),
            }}
          >
            <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
              {composedTitle}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color={aiColors.primary}>
              新建
            </Typography.Text>
          </View>
          {composed.proposal.coupon.items.slice(0, 3).map((item, index) => (
            <Typography.Text
              key={index}
              size={rpx(12)}
              color={aiColors.foreground}
            >
              · {item.label}
            </Typography.Text>
          ))}
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {couponPeriodText(composed)}
          </Typography.Text>
        </Pressable>
      ) : null}
      {choices.map(choice => {
        const active = choice.id === selected;
        return (
          <Pressable
            key={choice.id}
            accessibilityRole="radio"
            accessibilityState={{ selected: active, disabled: !interactive }}
            disabled={!interactive}
            onPress={() => setTapped(choice.id)}
            style={{
              padding: rpx(12),
              borderRadius: rpx(12),
              borderWidth: active ? 2 : 1,
              borderColor: active ? aiColors.primary : aiColors.border,
              backgroundColor: active
                ? tint(aiColors.primary, 0.04)
                : aiColors.card,
              gap: rpx(4),
            }}
          >
            <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
              {choice.title}
            </Typography.Text>
            {choice.items.slice(0, 3).map((item, index) => (
              <Typography.Text
                key={index}
                size={rpx(12)}
                color={aiColors.foreground}
              >
                · {couponItemText(item)}
              </Typography.Text>
            ))}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                {couponPeriodText(choice)}
              </Typography.Text>
              <Pressable
                accessibilityRole="link"
                onPress={() => setDetail(choice)}
                hitSlop={6}
              >
                <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
                  详情
                </Typography.Text>
              </Pressable>
            </View>
          </Pressable>
        );
      })}
      {interactive ? (
        <>
          {selected === kComposed && composedTitle ? (
            <PrimaryButton
              title="使用这个优惠券活动"
              onPress={() =>
                chat.send(`使用新建的优惠券活动「${composedTitle}」`, {
                  answer: { step: ApdmBuildStep.Which },
                })
              }
            />
          ) : choices.length > 0 ? (
            <PrimaryButton
              title="使用这个优惠券活动"
              disabled={!chosen}
              onPress={() =>
                chosen &&
                chat.send(`使用优惠券活动「${chosen.title}」`, {
                  answer: {
                    step: ApdmBuildStep.Which,
                    offer: {
                      kind: 'coupon_campaign',
                      couponCampaignId: chosen.id,
                    },
                  },
                })
              }
            />
          ) : null}
          <SecondaryButton
            title="新建优惠券活动"
            icon={CirclePlus}
            onPress={() => chat.send('新建一个优惠券活动')}
          />
          <Pressable
            accessibilityRole="button"
            onPress={() =>
              chat.send('不带优惠券，直接发送', {
                answer: { step: ApdmBuildStep.Which, offer: { kind: 'none' } },
              })
            }
            style={{ alignItems: 'center', paddingVertical: rpx(6) }}
          >
            <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
              不带优惠券，直接发送
            </Typography.Text>
          </Pressable>
        </>
      ) : null}
      <CouponCampaignDetailSheet
        coupon={detail}
        onClose={() => setDetail(null)}
      />
    </ChatCard>
  );
}
